import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Button, Flex, Input, Space, Table, Tag, Typography } from "antd";
import Title from "antd/es/typography/Title";
import { colors } from "../../../../../../constants/colors";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  dateObjToDispViewDate,
  timeObjToDispViewTimeWithSuffix,
} from "../../../../../../utils/utils";
import { getOffers } from "../../../../../../redux/services/offer/offerServices";
import { SearchOutlined } from "@ant-design/icons";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import styles from "./WalletTransactions.module.scss";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { getWalletInfoAction } from "../../../../../../redux/services/wallet-info/walletInfoServices";
import DescriptionDialog from "./DescriptionDialog";

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.guess()

const WalletTransactions = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* dialog dependencies */
  const [openDescriptionDialog, setOpenDescriptionDialog] = useState(false);
  /* dialog dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { walletInfo } = useSelector((state) => state.walletInfo);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: walletInfo?.transactions?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  const [description, setDescription] = useState("")
  /* table dependencies */

  useEffect(() => {
    handleGetTableData();
  }, [searchValue, pagination]);

  const handleGetTableData = () => {
    const dataToBeSent = {
      page: pagination?.pageNo,
      limit: pagination?.pageSize,
      search: searchValue,
    };
    dispatch(getWalletInfoAction(dataToBeSent, setLoading));
  };

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("walletInfo.transactionDate"),
        dataIndex: "transaction_date",
        key: "transaction_date",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.transaction_date, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("walletInfo.transactionTime"),
        dataIndex: "transaction_date",
        key: "transaction_date",
        render: (_, record) => (
          <p>
            {timeObjToDispViewTimeWithSuffix(dayjs.utc(record?.transaction_date).local().tz(dayjs.tz.guess()).format("YYYY-MM-DDTHH:mm:ss"))}
            {/* {timeObjToDispViewTimeWithSuffix(dayjs(record?.transaction_date).subtract(dayjs(record?.transaction_date).utcOffset(),"minute").format("YYYY-MM-DDTHH:mm:ss"))} */}
          </p>
        ),
      },
      {
        title: t("walletInfo.deposit"),
        dataIndex: "deposit",
        key: "deposit",
        render: (_, record) => (
          <p>{record?.deposit ? record?.deposit?.toFixed(2) : 0}</p>
        ),
      },
      {
        title: t("walletInfo.withdrawal"),
        dataIndex: "withdrawal",
        key: "withdrawal",
        render: (_, record) => (
          <p>{record?.withdrawal ? record?.withdrawal?.toFixed(2) : 0}</p>
        ),
      },
      {
        title: t("walletInfo.closing"),
        dataIndex: "closing_balance",
        key: "closing_balance",
        render: (_, record) => (
          <p>{record?.closing_balance ? record?.closing_balance?.toFixed(4) : 0}</p>
        ),
      },
      {
        title: t("walletInfo.description"),
        dataIndex: "description",
        key: "description",
        align: "center",
        render: (_, record) => {
          return (
            <>
                <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                  {t("view")}
                </Typography.Link>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleViewDetails = (e, record) => {
    setOpenDescriptionDialog(true);
    setDescription(record?.description)
  }

  const descriptionPropValues = useMemo(()=>({
    open : openDescriptionDialog,
    onClose : ()=>{
      setOpenDescriptionDialog(false)
      setDescription("")
    },
    description : description
  }),[openDescriptionDialog, description])

  return (
    <>
      <div>
        <Flex vertical gap="2rem">
          <Flex align="center" justify="space-between">
            <Title level={2}>{t("walletInfo.walletTransactions")}</Title>
          </Flex>
          <Table
            dataSource={walletInfo?.transactions?.data}
            columns={columns}
            pagination={paginationObj}
          />
        </Flex>
        <DescriptionDialog {...descriptionPropValues}/>
      </div>
    </>
  );
};

export default WalletTransactions;
